import { FC, useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormField,
  SpaceBetween,
  Box
} from '@cloudscape-design/components';
import { useOktaAuth } from '@okta/okta-react';
import type { GcpCert, GcpCertApproval } from '../../../types/gcpCert';
import updateCertApprovers from '../../../api/gcpCerts/updateCert';
import useAcceptGcpCert from '../../../hooks/mutate/useAcceptGcpCert';

interface ApprovalFormProps {
  gcpCert: GcpCert;
  userPermissions: string[];
  onSubmit: (updatedCert: GcpCert) => void;
}

const GcpCertApprovalForm: FC<ApprovalFormProps> = ({
  gcpCert,
  userPermissions,
  onSubmit
}) => {
  const { authState } = useOktaAuth();
  const thisUserEmail = authState?.idToken?.claims.email as string;

  const [appOwnerChecked, setAppOwnerChecked] = useState<boolean>(false);
  const [gcpTribeChecked, setGcpTribeChecked] = useState<boolean>(false);
  const [approvers, setApprovers] = useState<{ [key: string]: string | null }>({
    APPLICATION_OWNER: null,
    GCP_TRIBE: null
  });

  const appOwnerStatus = gcpCert.approvalStatus.find(a => a.name === 'APPLICATION_OWNER');
  const gcpTribeStatus = gcpCert.approvalStatus.find(a => a.name === 'GCP_TRIBE');

  const appOwnerApproved = appOwnerStatus?.status === 'APPROVED';
  const gcpTribeApproved = gcpTribeStatus?.status === 'APPROVED';

  const { mutateAsync: acceptGcpCert, isLoading } = useAcceptGcpCert(gcpCert.gcpCertID);

  const hasGcpTribePermission = userPermissions.some(
    perm =>
      perm.includes('gcpCerts:*:approve') ||
      perm.includes('gcpCerts:*:*') ||
      perm.includes('*:*:*')
  );

  // Initialize checkboxes and approvers
  useEffect(() => {
    setAppOwnerChecked(appOwnerApproved);
    setGcpTribeChecked(gcpTribeApproved);
    setApprovers({
      APPLICATION_OWNER: appOwnerStatus?.approverID || null,
      GCP_TRIBE: gcpTribeStatus?.approverID || null
    });
  }, [gcpCert]);

  /** ✅ Checkbox Handler (UI only) */
  const handleCheckboxChange = async (type: 'APPLICATION_OWNER' | 'GCP_TRIBE', checked: boolean) => {
    if (type === 'APPLICATION_OWNER') setAppOwnerChecked(checked);
    if (type === 'GCP_TRIBE') setGcpTribeChecked(checked);

    setApprovers(prev => ({
      ...prev,
      [type]: checked ? thisUserEmail : ''
    }));

    // Update individual approval state in backend (intermediate update)
    const updatedApprovalObj: GcpCertApproval = {
      name: type,
      status: checked ? 'APPROVED' : 'PENDING_APPROVAL',
      approverID: checked ? thisUserEmail : '',
      lastUpdatedTime: new Date().toISOString(),
    };

    await updateCertApprovers({
      action: 'UPDATE_APPROVAL',
      gcpCertID: gcpCert.gcpCertID,
      approvalStatus: updatedApprovalObj
    });
  };

  /** Finalize approval — sends APPROVE (triggers member-projects push), not just checkbox UPDATE_APPROVAL. */
  const handleApprove = async () => {
    if (!(appOwnerChecked && gcpTribeChecked)) return;

    try {
      // Checkboxes only send UPDATE_APPROVAL (approver row). APPROVE must be sent explicitly.
      // Skip only when the cert is already fully approved at top level.
      if (gcpCert.status !== 'APPROVED') {
        if (appOwnerChecked) {
          await acceptGcpCert({
            action: 'APPROVE',
            gcpCertID: gcpCert.gcpCertID,
            approverID: thisUserEmail,
            details: { type: 'APPLICATION_OWNER' },
            timestamp: new Date().toISOString()
          });
        }

        if (gcpTribeChecked) {
          await acceptGcpCert({
            action: 'APPROVE',
            gcpCertID: gcpCert.gcpCertID,
            approverID: thisUserEmail,
            details: { type: 'GCP_TRIBE' },
            timestamp: new Date().toISOString()
          });
        }
      }

      const updatedCert: GcpCert = {
        ...gcpCert,
        approvalStatus: gcpCert.approvalStatus.map(a => {
          if (a.name === 'APPLICATION_OWNER' && appOwnerChecked)
            return { ...a, status: 'APPROVED', approverID: thisUserEmail };
          if (a.name === 'GCP_TRIBE' && gcpTribeChecked)
            return { ...a, status: 'APPROVED', approverID: thisUserEmail };
          return a;
        }),
        status: 'APPROVED'
      };

      onSubmit(updatedCert);
    } catch (error) {
      console.error('Error approving GCP Cert:', error);
    }
  };

  return (
    <Container header={<h3>Approve GCP Certificate</h3>}>
      <SpaceBetween size="l">

        {/* Application Owner Approval */}
        <FormField label="Application Owner Approval">
          <Box>
            <input
              type="checkbox"
              checked={appOwnerChecked}
              onChange={(e) => handleCheckboxChange('APPLICATION_OWNER', e.target.checked)}
              disabled={appOwnerApproved || thisUserEmail !== gcpCert.details.applicationOwnerEmail}
            />
            <span> (Approved by: {approvers.APPLICATION_OWNER || '-'})</span>
          </Box>
        </FormField>

        {/* GCP Tribe Approval */}
        <FormField label="GCP Tribe Approval">
          <Box>
            <input
              type="checkbox"
              checked={gcpTribeChecked}
              onChange={(e) => handleCheckboxChange('GCP_TRIBE', e.target.checked)}
              disabled={gcpTribeApproved || !hasGcpTribePermission}
            />
            <span> (Approved by: {approvers.GCP_TRIBE || '-'})</span>
          </Box>
        </FormField>

        {/* Action Buttons */}
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => onSubmit(gcpCert)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isLoading}
            disabled={!(appOwnerChecked && gcpTribeChecked)}
            onClick={handleApprove}
          >
            Approve
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

export default GcpCertApprovalForm;